import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { Resizable } from 're-resizable';
import { Link } from 'react-router-dom';
import { useConfig } from '../../contexts/ConfigContext';

// Import the API service - removing the unused SearchResponse import
import { DocumentService, SearchResult, getAuthHeaders } from '../../utils/apiService';

interface Source {
  content: string;
  metadata: {
    file_name: string;
    file_id?: string;
    version_id?: string;
    content_type?: string;
    collection_name?: string;
    [key: string]: any;
  };
}

// Add new interface for Google Workspace view response
interface GoogleWorkspaceViewResponse {
  type: 'google_workspace';
  view_url: string;
  file_name: string;
  content_type: string;
}

interface ErrorViewResponse {
  type: 'error';
  message: string;
}

type ViewResponse = GoogleWorkspaceViewResponse | ErrorViewResponse;

const SearchPage: React.FC = () => {
  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const { configurations } = useConfig();
  
  // States for QA search functionality
  const [qaQuery, setQaQuery] = useState<string>('');
  const [isQaLoading, setIsQaLoading] = useState<boolean>(false);
  const [qaError, setQaError] = useState<string | null>(null);
  const [qaAnswer, setQaAnswer] = useState<string>('');
  const [qaSources, setQaSources] = useState<Source[]>([]);
  
  // State for currently selected document to view
  const [selectedSourceInfo, setSelectedSourceInfo] = useState<(Source['metadata'] | SearchResult['metadata']) | null>(null);
  
  // States for document viewing
  const [selectedDocumentContent, setSelectedDocumentContent] = useState<string | null>(null);
  const [presignedUrl, setPresignedUrl] = useState<string | null>(null);
  const [isViewerLoading, setIsViewerLoading] = useState<boolean>(false);
  const [viewerError, setViewerError] = useState<string | null>(null);

  // State to track which search tab is active
  const [activeTab, setActiveTab] = useState<'semantic' | 'qa'>('semantic');
  
  // State to track embeddings status
  const [isEmbeddingCheck, setIsEmbeddingCheck] = useState<boolean>(false);
  const [hasEmbeddings, setHasEmbeddings] = useState<boolean>(false);
  const [isEmbedding, setIsEmbedding] = useState<boolean>(false);

  // Add new state for Google Workspace view
  const [googleWorkspaceView, setGoogleWorkspaceView] = useState<GoogleWorkspaceViewResponse | null>(null);
  
  // State to track panel width
  const [leftPanelWidth, setLeftPanelWidth] = useState<string | number>("40%");
  const [rightPanelWidth, setRightPanelWidth] = useState<string | number>("60%");

  // Handle resize
  const handleResize = (_: any, __: any, ref: any) => {
    // Calculate percentage width
    const containerWidth = document.querySelector(".flex.h-\\[calc\\(100vh-theme\\(spacing\\.16\\)\\)\\]")?.clientWidth || 0;
    if (containerWidth > 0) {
      const newLeftWidth = ((ref.clientWidth / containerWidth) * 100) + "%";
      const newRightWidth = ((100 - (ref.clientWidth / containerWidth) * 100)) + "%";
      setLeftPanelWidth(newLeftWidth);
      setRightPanelWidth(newRightWidth);
    }
  };

  // Check if there are any embedded documents
  const checkEmbeddings = async () => {
    // Rate limiting - don't check too frequently
    const now = Date.now();
    const lastCheckKey = 'last_embedding_check';
    const lastCheck = localStorage.getItem(lastCheckKey);
    
    if (lastCheck && (now - parseInt(lastCheck)) < 10000) {
      console.log('Skipping embedding check - too soon since last check');
      return;
    }
    
    localStorage.setItem(lastCheckKey, now.toString());
    
    setIsEmbeddingCheck(true);
    try {
      // Get embedding stats using DocumentService instead of axios
      const response = await DocumentService.getEmbeddingStats();
      
      if (response.success) {
        // We have embedded documents if the embedded count is greater than 0
        setHasEmbeddings(response.embedded_documents > 0);
        
        if (response.embedded_documents === 0) {
          setSearchError('Nessun documento indicizzato. Clicca su "Indicizza tutti i documenti" per iniziare.');
        } else if (response.embedded_documents > 0 && response.total_documents > response.embedded_documents) {
          setSearchError(`${response.embedded_documents} documenti indicizzati su ${response.total_documents}. Puoi completare l'indicizzazione cliccando su "Indicizza tutti i documenti".`);
        }
      } else {
        setHasEmbeddings(false);
        setSearchError('Impossibile verificare lo stato dell\'indicizzazione. ' + response.message);
      }
    } catch (err: any) {
      console.error('Error checking embeddings:', err);
      setHasEmbeddings(false);
      
      // Extract error details for a more informative message
      const errorDetails = err.message || 'Errore sconosciuto';
      
      // Check if this is a configuration error
      if (errorDetails.includes("No active configuration") || 
          errorDetails.includes("Please set up a database configuration") || 
          errorDetails.includes("configurazione") || 
          errorDetails.includes("config")) {
        setSearchError('È necessario configurare un database prima di poter utilizzare la ricerca. ' +
          'Vai alla pagina di configurazione per impostare la tua prima configurazione con una cartella Google Drive.');
      } else if (errorDetails.includes("authentication") || errorDetails.includes("login") || errorDetails.includes("401")) {
        // Authentication error
        setSearchError('Sessione scaduta o non autorizzata. Effettua nuovamente il login.');
      } else {
        setSearchError('Errore durante la verifica dell\'indicizzazione: ' + errorDetails);
      }
    } finally {
      setIsEmbeddingCheck(false);
    }
  };
  
  // Embed all documents
  const embedAllDocuments = async () => {
    try {
      setIsEmbedding(true);
      
      // Use the async version for better performance and authentication
      const response = await DocumentService.embedAllDocumentsAsync();
      
      if (response.success) {
        const successCount = response.successful_count || 0;
        const totalCount = response.total_count || 0;
        
        if (successCount > 0) {
          setSearchError(`${successCount} documenti indicizzati con successo su ${totalCount}. Ora puoi cercare nel contenuto.`);
          setHasEmbeddings(true);
        } else if (totalCount === 0) {
          setSearchError('Nessun documento trovato da indicizzare. Verifica che ci siano documenti nella cartella configurata.');
        } else {
          setSearchError('Nessun documento è stato indicizzato con successo. Verifica la connessione e riprova.');
        }
      } else {
        setSearchError(`Errore nell'indicizzazione dei documenti: ${response.message}`);
      }
    } catch (err: any) {
      console.error('Error embedding all documents:', err);
      
      // Check for specific error types
      if (err.message && err.message.includes('authentication')) {
        setSearchError('Errore di autenticazione. Effettua nuovamente il login.');
      } else if (err.message && err.message.includes('configuration')) {
        setSearchError('Errore di configurazione. Verifica che sia stata impostata una configurazione valida.');
      } else {
        const errorMessage = err.message || 'Si è verificato un errore durante l\'indicizzazione';
        setSearchError(`Errore durante l'indicizzazione: ${errorMessage}`);
      }
    } finally {
      setIsEmbedding(false);
    }
  };
  
  // Check for embeddings on component mount
  useEffect(() => {
    checkEmbeddings();
  }, []);

  // Add a check for configuration error from the searchError state
  const isConfigError = searchError ? 
    searchError.includes('configurazione') || 
    searchError.includes('configura') || 
    searchError.includes('database') 
    : false;
    
  // Add a check for authentication errors
  const isAuthError = searchError ?
    searchError.includes('login') ||
    searchError.includes('sessione') ||
    searchError.includes('autenticare') ||
    searchError.includes('autorizzata')
    : false;

  // If we detect an authentication error, redirect to login after a short delay
  useEffect(() => {
    if (isAuthError) {
      const timer = setTimeout(() => {
        window.location.href = '/login';
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, [isAuthError]);

  // Handle direct semantic search using embeddings
  const handleSemanticSearch = async () => {
    if (!query.trim()) {
      setSearchError('Per favore, inserisci un termine di ricerca');
      return;
    }

    setIsLoading(true);
    setSearchError(null);
    setSearchResults([]);
    setSelectedSourceInfo(null);
    setSelectedDocumentContent(null);
    setPresignedUrl(null);
    setViewerError(null);

    try {
      const response = await DocumentService.searchDocuments(query);
      
      if (!response.success) {
        // Handle specific error messages
        if (response.message && response.message.includes('No documents are embedded')) {
          setSearchError('Nessun documento è stato indicizzato. Clicca su "Indicizza tutti i documenti" per iniziare.');
          setHasEmbeddings(false);
        } else if (response.message && response.message.includes('Documents are indexed but may not be searchable')) {
          setSearchError('I documenti sono indicizzati ma non risultano ricercabili. Prova a ri-indicizzare i documenti.');
          setHasEmbeddings(true);
        } else {
          setSearchError(response.message || 'Errore durante la ricerca');
        }
        return;
      }
      
      setSearchResults(response.results);
      
      if (response.results.length === 0) {
        // Handle no results found
        setSearchError('Nessun risultato trovato per la tua ricerca. Prova a modificare i termini di ricerca o a indicizzare più documenti.');
      } else {
        // Automatically select the first result if available
        if (response.results.length > 0) {
          handleSourceClick(response.results[0].metadata);
        }
      }
    } catch (err: any) {
      console.error('Error in semantic search:', err);
      
      // Check if we have a structured error response
      if (err.response && err.response.data) {
        const errorData = err.response.data;
        if (errorData.detail && typeof errorData.detail === 'string') {
          setSearchError(errorData.detail);
        } else if (errorData.message) {
          setSearchError(errorData.message);
        } else {
          setSearchError('Errore durante la ricerca: ' + (err.message || 'Errore sconosciuto'));
        }
      } else {
        setSearchError('Impossibile eseguire la ricerca: ' + (err.message || 'Errore sconosciuto'));
      }
      
      // If we get an authentication error, we might need to re-authenticate
      if (err.response && err.response.status === 401) {
        setSearchError('Sessione scaduta. Effettua nuovamente il login.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Handle QA search - original functionality
  const handleQASearch = async () => {
    if (!qaQuery.trim()) {
      setQaError('Per favore, inserisci una domanda');
      return;
    }

    setIsQaLoading(true);
    setQaError(null);
    setQaAnswer('');
    setQaSources([]);
    setSelectedSourceInfo(null);
    setSelectedDocumentContent(null); 
    setPresignedUrl(null);
    setViewerError(null);

    try {
      // Use EventSource for streaming
      const eventSource = new EventSource(`/api/v1/langchain/qa/stream?query=${encodeURIComponent(qaQuery)}`);
      
      // Handle different streaming events
      eventSource.addEventListener('start', () => {
        // Stream started, already in loading state

      });
      
      eventSource.addEventListener('token', (e) => {
        const data = JSON.parse(e.data);
        setQaAnswer(prev => prev + data.token);
      });
      
      eventSource.addEventListener('complete', (e) => {
        const data = JSON.parse(e.data);
        const sources = Array.isArray(data.sources) ? data.sources : [];
        setQaSources(sources);
        
        // Automatically select the first source if available
        if (sources.length > 0 && sources[0]?.metadata) {
          handleSourceClick(sources[0].metadata);
        } else if (sources.length === 0) {
          setQaError('Nessuna fonte trovata per questa domanda. Prova a indicizzare i documenti prima di fare domande.');
        }
        
        // Set loading to false when complete
        setIsQaLoading(false);
        
        // Close the event source
        eventSource.close();
      });
      
      eventSource.addEventListener('error', (e) => {
        // Handle errors from the server
        eventSource.close();
        console.error('Stream error:', e);
        setQaError('Errore durante l\'elaborazione della tua domanda. Riprova più tardi.');
        setIsQaLoading(false);
      });
      
      // Handle connection errors
      eventSource.onerror = (error) => {
        eventSource.close();
        console.error('EventSource error:', error);
        setQaError('Errore di connessione durante l\'elaborazione della tua domanda.');
        setIsQaLoading(false);
      };
      
    } catch (err: any) {
      console.error('Error in QA search:', err);
      setQaError(err.response?.data?.detail || 'Impossibile elaborare la tua domanda');
      setIsQaLoading(false);
    }
  };

  // Function to handle source selection
  const handleSourceClick = (metadata: (Source['metadata'] | SearchResult['metadata'])) => {
    setSelectedSourceInfo(metadata);
    // Clear previous content/URL states immediately
    setSelectedDocumentContent(null); 
    setPresignedUrl(null);
    setViewerError(null); 
    setIsViewerLoading(true); // Set loading state
    // Content/URL fetching will be handled by useEffect
  };
  
  // Update the useEffect that fetches document content
  useEffect(() => {
    const fetchViewerData = async () => {
      // Clear previous state
      setSelectedDocumentContent(null);
      setPresignedUrl(null);
      setViewerError(null);
      setGoogleWorkspaceView(null);
      setIsViewerLoading(true);
      
      if (!selectedSourceInfo) {
        setIsViewerLoading(false);
        return;
      }
      
      const { file_name, file_id, version_id, content_type } = selectedSourceInfo;
      
      const docx_mimetype = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

      // For Google Workspace files, we'll handle things differently
      if (file_id) {
        try {
          // Use fetch with proper authentication headers instead of axios
          const response = await fetch(`/api/v1/documents/view/${file_id}`, {
            method: 'GET',
            headers: {
              ...getAuthHeaders(),
            },
            credentials: 'include'
          });
          
          // Check if the response is JSON (likely for Google Workspace files)
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            // Parse the JSON response
            const jsonData = await response.json() as ViewResponse;
            if (jsonData.type === 'google_workspace') {
              setGoogleWorkspaceView(jsonData);
            } else if (jsonData.type === 'error') {
              console.error(`Google Workspace view error: ${jsonData.message}`);
              setViewerError(jsonData.message);
            }
            setIsViewerLoading(false);
            return;
          }
          
          // For PDF and other viewable files, create a blob URL
          const blob = await response.blob();
          const url = URL.createObjectURL(blob);
          setPresignedUrl(url);
          setIsViewerLoading(false);
          return;
        } catch (err: any) {
          console.error("Error fetching document for viewing:", err);
          setViewerError(err.message || 'Impossibile caricare l\'anteprima del documento.');
          setIsViewerLoading(false);
          return;
        }
      }

      // Skip fetching for PDF (handled by iframe src directly)
      if (content_type === 'application/pdf') {
        setIsViewerLoading(false);
        return;
      }

      // Fetch Pre-signed URL for DOCX
      if (content_type === docx_mimetype) {
        try {
          const response = await DocumentService.getPresignedGetUrl(file_name, version_id);
          setPresignedUrl(response.url);
        } catch (err: any) {
          console.error("Error fetching presigned URL:", err);
          setViewerError(err.message || 'Impossibile ottenere l\'URL del documento per l\'anteprima.');
          setPresignedUrl(null);
        }
      } else {
        // Fetch text content for other non-PDF/DOCX types
        try {
          // If we have a file_id, use it to get content
          if (file_id) {
            const response = await fetch(`/api/v1/documents/${file_id}/content`, {
              method: 'GET',
              headers: getAuthHeaders(),
              credentials: 'include'
            });
            
            if (!response.ok) {
              const errorText = await response.text();
              let detail = errorText;
              try {
                const errorJson = JSON.parse(errorText);
                detail = errorJson.detail || errorJson.message || errorText;
              } catch (e) { /* Ignore parsing error */ }
              throw new Error(detail);
            }
            
            const data = await response.json();
            setSelectedDocumentContent(data.content);
          } else {
            const contentResponse = await DocumentService.getDocumentContent(file_name, version_id);
            setSelectedDocumentContent(contentResponse.content);
          }
        } catch (err: any) {
          console.error("Error fetching document content:", err);
          const detail = err.message || 'Impossibile caricare il contenuto del documento.';
          setViewerError(detail);
          setSelectedDocumentContent(null);
        }
      }

      setIsViewerLoading(false);
    };

    fetchViewerData();
  }, [selectedSourceInfo]);

  return (
    <div className="flex h-[calc(100vh-theme(spacing.16))] overflow-hidden">
      
      {/* Show auth error message */}
      {isAuthError && (
        <div className="w-full h-full flex items-center justify-center">
          <div className="max-w-lg p-8 bg-white shadow-lg rounded-lg text-center">
            <svg className="w-20 h-20 mx-auto text-orange-500 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} 
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <h2 className="text-xl font-semibold text-gray-800 mb-2">Autenticazione richiesta</h2>
            <p className="text-gray-600 mb-6">
              La tua sessione è scaduta o non sei autenticato. È necessario effettuare il login per accedere a questa funzionalità.
            </p>
            
            <div className="mb-6 text-left bg-orange-50 p-4 rounded-md border border-orange-100">
              <p className="text-orange-700">
                Verrai reindirizzato alla pagina di login automaticamente tra pochi secondi.
                Se non vieni reindirizzato, clicca sul pulsante qui sotto.
              </p>
            </div>
            
            <Link
              to="/login"
              className="inline-flex items-center justify-center px-5 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2"
            >
              <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                  d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
              </svg>
              Vai alla pagina di Login
            </Link>
          </div>
        </div>
      )}
      
      {/* Show friendly configuration message when configuration error is detected */}
      {isConfigError && (
        <div className="w-full h-full flex items-center justify-center">
          <div className="max-w-lg p-8 bg-white shadow-lg rounded-lg text-center">
            <svg className="w-20 h-20 mx-auto text-blue-500 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} 
                d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
            </svg>
            <h2 className="text-xl font-semibold text-gray-800 mb-2">Configurazione necessaria</h2>
            <p className="text-gray-600 mb-6">
              Per utilizzare la ricerca semantica, è necessario configurare una connessione a Google Drive e creare un database vettoriale.
            </p>
            
            <div className="mb-6 text-left bg-blue-50 p-4 rounded-md border border-blue-100">
              <h3 className="font-medium text-blue-800 mb-2">Come configurare:</h3>
              <ol className="list-decimal pl-5 text-blue-700 space-y-1">
                <li>Vai alla pagina Configurazioni</li>
                <li>Collega una cartella Google Drive</li>
                <li>Crea un database vettoriale</li>
                <li>Attendi l'indicizzazione dei documenti</li>
              </ol>
            </div>
            
            <Link
              to="/user-config"
              className="inline-flex items-center justify-center px-5 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                  d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              </svg>
              Vai alla pagina Configurazioni
            </Link>
          </div>
        </div>
      )}
      
      {/* Show regular search interface when no configuration error */}
      {!isConfigError && (
        <>
          {/* Left Column: Search Interface */}
          <Resizable
            size={{ width: leftPanelWidth, height: "100%" }}
            minWidth="20%"
            maxWidth="80%"
            enable={{ right: true }}
            onResizeStop={handleResize}
            handleClasses={{ right: "resize-handle-right" }}
            className="bg-white shadow-md flex-shrink-0 flex flex-col overflow-hidden"
            handleStyles={{ 
              right: { 
                width: '8px', 
                right: '0', 
                cursor: 'col-resize', 
                backgroundColor: '#e5e7eb',
                height: '100%'
              } 
            }}
          >
            <div className="p-6 overflow-y-auto h-full w-full flex flex-col">
              
              {/* Tab switcher */}
              <div className="flex border-b mb-6">
                <button 
                  className={`flex-1 py-2 font-medium ${activeTab === 'semantic' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
                  onClick={() => setActiveTab('semantic')}
                >
                  Cerca Documenti
                </button>
                <button 
                  className={`flex-1 py-2 font-medium ${activeTab === 'qa' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
                  onClick={() => setActiveTab('qa')}
                >
                  Interroga Documenti
                </button>
              </div>
              
              {/* Embeddings status banner */}
              {!isEmbeddingCheck && !hasEmbeddings && (
                <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded mb-4 flex flex-col">
                  <div className="flex">
                    <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <span>Nessun documento indicizzato trovato. La ricerca non produrrà risultati.</span>
                  </div>
                  <button
                    className={`mt-2 w-full p-2 rounded text-white ${
                      isEmbedding ? 'bg-blue-300' : 'bg-blue-600 hover:bg-blue-700'
                    }`}
                    onClick={embedAllDocuments}
                    disabled={isEmbedding}
                  >
                    {isEmbedding ? 'Indicizzazione in corso...' : 'Indicizza tutti i documenti'}
                  </button>
                </div>
              )}
              
              {activeTab === 'semantic' ? (
                /* Semantic Search Tab Content */
                <div>
                  {searchError && (
                    <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800/50 rounded-lg text-red-700 dark:text-red-400">
                      <p>{searchError}</p>
                      {(searchError.includes('configurazione') || searchError.includes('configura') || searchError.includes('database')) && (
                        <Link 
                          to="/user-config"
                          className="mt-3 inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                        >
                          <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                              d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                          </svg>
                          Vai alla pagina di Configurazione
                        </Link>
                      )}
                      {searchError.includes('login') && (
                        <Link 
                          to="/login"
                          className="mt-3 inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                        >
                          <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                          </svg>
                          Effettua il login
                        </Link>
                      )}
                    </div>
                  )}

                  <div className="mb-6">
                    <label htmlFor="search-input" className="block text-gray-700 mb-2">Termini di Ricerca</label>
                    <input
                      id="search-input"
                      className="w-full p-2 border rounded"
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      placeholder="Cerca nei documenti..."
                      onKeyDown={(e) => e.key === 'Enter' && handleSemanticSearch()}
                    />
                  </div>

                  <button
                    className={`w-full p-3 rounded text-white mb-6 ${isLoading ? 'bg-blue-300' : 'bg-blue-600 hover:bg-blue-700'}`}
                    onClick={handleSemanticSearch}
                    disabled={isLoading || !query.trim() || isEmbedding}
                  >
                    {isLoading ? 'Ricerca in corso...' : 'Cerca'}
                  </button>

                  {/* Search Results */}
                  {searchResults && searchResults.length > 0 && (
                    <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                          Risultati della ricerca ({searchResults.length})
                        </h3>
                        {configurations.length > 1 && (
                          <span className="text-sm text-gray-500 dark:text-gray-400">
                            Cercato in {configurations.filter(c => c.is_active).length} cartelle
                          </span>
                        )}
                      </div>
                      <div className="space-y-3 overflow-y-auto flex-grow pr-2">
                        {searchResults.map((result, index) => {
                          const fileName = result.metadata?.file_name || "[Nome File Mancante]";
                          const filePath = result.metadata?.file_path;
                          const resultContent = result.content || "[Contenuto Mancante]";
                          const score = result.score;
                          
                          return (
                            <div 
                              key={index} 
                              className={`border rounded p-3 hover:bg-gray-100 cursor-pointer ${
                                selectedSourceInfo?.file_name === fileName
                                  ? 'bg-blue-100 border-blue-300' 
                                  : 'border-gray-300'
                              }`}
                              onClick={() => handleSourceClick(result.metadata)}
                            >
                              <div className="font-medium mb-1">Documento: {fileName}</div>
                              {filePath && (
                                <div className="text-sm text-blue-600 mb-2 font-medium">
                                  <span className="mr-1">📁</span>
                                  <span className="mr-1">Percorso:</span>
                                  {filePath}
                                </div>
                              )}
                              {score !== undefined && (
                                <div className="text-xs text-gray-500 mb-1">
                                  Rilevanza: {Math.round(score * 100)}%
                                </div>
                              )}
                              <div className="text-sm text-gray-700 line-clamp-3">
                                <ReactMarkdown>{resultContent}</ReactMarkdown>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                /* QA Search Tab Content */
                <div className="flex-grow flex flex-col">
                  {qaError && (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                      {qaError}
                    </div>
                  )}

                  <div className="mb-6">
                    <label htmlFor="question-input" className="block text-gray-700 mb-2">La tua Domanda</label>
                    <textarea
                      id="question-input"
                      className="w-full p-2 border rounded h-24"
                      value={qaQuery}
                      onChange={(e) => setQaQuery(e.target.value)}
                      placeholder="Fai una domanda sui tuoi documenti..."
                    />
                  </div>

                  <button
                    className={`w-full p-3 rounded text-white mb-6 ${isQaLoading ? 'bg-blue-300' : 'bg-blue-600 hover:bg-blue-700'}`}
                    onClick={handleQASearch}
                    disabled={isQaLoading || !qaQuery.trim() || isEmbedding}
                  >
                    {isQaLoading ? 'Elaborazione...' : 'Chiedi'}
                  </button>

                  {/* Show answer section if there's a response or we're loading */}
                  {(qaAnswer || isQaLoading) && (
                    <div className="mt-6 space-y-8 flex-grow overflow-hidden flex flex-col">
                      <div className="border-2 border-blue-300 bg-blue-50 rounded-lg p-4 shadow-md">
                        <h3 className="text-lg font-semibold mb-3 flex items-center text-blue-700 border-b border-blue-200 pb-2">
                          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          Risposta
                          {isQaLoading && (
                            <span className="ml-2 inline-block w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></span>
                          )}
                        </h3>
                        {qaAnswer ? (
                          <div className="text-gray-800 prose prose-sm max-w-none">
                            <ReactMarkdown>{qaAnswer}</ReactMarkdown>
                          </div>
                        ) : (
                          <p className="text-gray-800">{isQaLoading ? "Elaborazione..." : "[Nessuna Risposta Ricevuta]"}</p>
                        )}
                      </div>
                      
                      {/* Check if qaSources is an array and has items before mapping */}
                      {Array.isArray(qaSources) && qaSources.length > 0 && (
                        <div className="flex-grow overflow-hidden flex flex-col bg-gray-50 border-2 border-gray-300 rounded-lg p-4 shadow-md">
                          <h3 className="text-lg font-semibold mb-3 flex items-center text-gray-700 border-b border-gray-200 pb-2">
                            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2" />
                            </svg>
                            Fonti
                          </h3>
                          <div className="space-y-3 overflow-y-auto flex-grow pr-2">
                            {qaSources.map((source, index) => {
                              // Safely access metadata and file_name
                              const fileName = source?.metadata?.file_name || "[Nome File Mancante]";
                              const filePath = source?.metadata?.file_path;
                              const sourceContent = source?.content || "[Contenuto Mancante]";
                              const versionId = source?.metadata?.version_id;
                              const fileId = source?.metadata?.file_id;

                              return (
                                <div 
                                  key={index} 
                                  className={`border rounded p-3 hover:bg-gray-100 cursor-pointer ${
                                    (fileId && selectedSourceInfo?.file_id === fileId) || 
                                    (selectedSourceInfo?.file_name === fileName && selectedSourceInfo?.version_id === versionId)
                                      ? 'bg-blue-100 border-blue-300' 
                                      : 'border-gray-300'
                                  }`}
                                  onClick={() => source?.metadata && handleSourceClick(source.metadata)} 
                                >
                                  <div className="font-medium mb-1">Documento: {fileName}</div>
                                  {filePath && (
                                    <div className="text-sm text-blue-600 mb-2 font-medium">
                                      <span className="mr-1">📁</span>
                                      <span className="mr-1">Percorso:</span>
                                      {filePath}
                                    </div>
                                  )}
                                  <div className="text-sm text-gray-700 line-clamp-3">
                                    <ReactMarkdown>{sourceContent}</ReactMarkdown>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </Resizable>
          
          {/* Right Column: Document Viewer */}
          <div style={{ width: rightPanelWidth }} className="p-6 border-l border-gray-200 overflow-y-auto flex flex-col">
            {/* Content Area */} 
            <div className="bg-gray-100 dark:bg-gray-800 rounded flex-grow overflow-y-auto flex flex-col">
              {selectedSourceInfo ? (
                (() => {
                   const { file_name, file_id, version_id, content_type } = selectedSourceInfo;
                   const docx_mimetype = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

                   // === Loading State ===
                   if (isViewerLoading) {
                     return (
                       <div className="flex items-center justify-center h-full p-4">
                         <div className="w-12 h-12 border-4 border-blue-600 dark:border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                         <p className="ml-4 text-gray-600 dark:text-gray-300">Caricamento anteprima...</p>
                       </div>
                     );
                   }

                   // === Error State ===
                   if (viewerError) {
                     return (
                       <div className="text-center text-red-600 p-4">
                         <p>Errore caricamento anteprima:</p>
                         <p className="text-sm mt-1">{viewerError}</p>
                       </div>
                     );
                   }
                   
                   // === Google Workspace Viewer ===
                   if (googleWorkspaceView) {
                     return (
                       <div className="flex flex-col h-full">
                         <div className="p-2 bg-gray-200 text-gray-800 text-sm">
                           <span>Visualizzazione di: {googleWorkspaceView.file_name}</span>
                         </div>
                         <div className="flex-grow flex flex-col">
                           <iframe
                             src={googleWorkspaceView.view_url}
                             title={`Google Workspace Viewer: ${googleWorkspaceView.file_name}`}
                             className="w-full h-full border-0 flex-grow"
                           />
                           <div className="p-2 bg-gray-200 border-t border-gray-300">
                             <a 
                               href={googleWorkspaceView.view_url} 
                               target="_blank" 
                               rel="noopener noreferrer"
                               className="text-blue-600 hover:text-blue-800 flex items-center justify-center"
                             >
                               <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                               </svg>
                               Apri in Google Drive
                             </a>
                           </div>
                         </div>
                       </div>
                     );
                   }
                   
                   // === PDF Rendering ===
                   if (content_type === 'application/pdf' || presignedUrl) {
                     let viewUrl = presignedUrl;
                     
                     if (!viewUrl) {
                       // Always prefer file_id over file_name if available
                       if (file_id) {
                         viewUrl = `/api/v1/documents/view/${file_id}`;

                       } else if (file_name) {
                         const encodedFileName = encodeURIComponent(file_name);
                         const queryParams = version_id ? `?version_id=${version_id}` : '';
                         viewUrl = `/api/v1/documents/view/${encodedFileName}${queryParams}`;

                       } else {
                         // Handle the case where neither file_id nor file_name is available

                         return (
                           <div className="text-center text-red-600 p-4">
                             <p>Errore: Informazioni documento mancanti</p>
                             <p className="text-sm mt-1">Impossibile visualizzare il documento senza identificativo.</p>
                           </div>
                         );
                       }
                     }
                     
                     // Special handling for DOCX files with presigned URLs
                     if (content_type === docx_mimetype && presignedUrl) {
                       // Use Google Docs Viewer for DOCX files
                       const viewerUrl = `https://docs.google.com/gview?url=${encodeURIComponent(presignedUrl)}&embedded=true`;
                       return (
                         <iframe
                           src={viewerUrl}
                           title={`Anteprima DOCX per ${file_name}`}
                           className="w-full h-full border-0 flex-grow"
                         />
                       );
                     }
                     
                     // For PDFs and other file types with a view URL
                     return (
                       <iframe
                         src={viewUrl}
                         title={`Anteprima per ${file_name}`}
                         className="w-full h-full border-0 flex-grow"
                       />
                     );
                   } 
                   
                   // === Other Text Rendering ===
                   if (selectedDocumentContent !== null) {
                     return (
                       <pre className="whitespace-pre-wrap text-sm text-gray-800 dark:text-gray-200 p-4 flex-grow overflow-auto">
                         {selectedDocumentContent || "[Nessun contenuto testuale estratto]"}
                       </pre>
                     );
                   }
                   
                   // Fallback
                   return (
                     <p className="text-gray-500 italic text-center mt-4 p-4">
                       Impossibile caricare il contenuto del documento.
                     </p>
                   );
                })()
              ) : (
                <p className="text-gray-400 italic text-center mt-10">
                  Clicca su un documento nei risultati a sinistra per visualizzarne il contenuto qui.
                </p>
              )}
            </div>
          </div> {/* End Right Column */}
        </>
      )}
    </div> // End Main Flex Container
  );
};

export default SearchPage; 